/**
 * DEFINED BENEFIT PENSIONS (owner, 12–15 Oct 2026; built for the UniSuper conversation).
 *
 * A lifetime pension paid from a super fund by a formula — UniSuper's Defined Benefit Division, CSS,
 * PSS, the military schemes, the state schemes — not bought with a lump sum. It has no account
 * balance behind it. The profile records it as an INCOME entry (type `definedBenefitPension`), and
 * this module holds every rule the engine applies to it. Nothing here is ever sold, commuted or
 * restructured by a strategy.
 *
 * THE RULES, each from the Social Security Guide page the owner supplied on 15 Oct 2026:
 *
 *   - INCOME TEST (4.9.3.30): assessable income = annual payment − deductible amount, and from
 *     1 January 2016 the deductible amount is capped at 10% of the gross payment. The three military
 *     schemes (DFRDB, MilitarySuper, DFRB) are excluded from the cap. The same whether or not the
 *     pension is counted as an asset.
 *   - ASSETS TEST, EXEMPT (4.9.2.20): a LIFETIME defined benefit pension is indefinitely exempt where
 *     the scheme was established before 20 September 1998, provided the product before then, the
 *     product was exempt then, and the member's pension comes from that pre-1998 product — the date
 *     the member's own pension started does not matter. DFRDB and MilitarySuper are exempt outright.
 *   - ASSETS TEST, COUNTED (4.9.3.30, 4.9.5.60): a pension that is NOT exempt is valued at the annual
 *     payment × the pension valuation factor for the person's age next birthday and the pension's
 *     indexation rate (Social Security (Pension Valuation Factor) Determination 2018), reassessed
 *     every year.
 *   - TAX (ATO, defined benefit income cap): from age 60 the payment is tax‑free up to the cap
 *     ($131,250 for 2026‑27 = general transfer balance cap ÷ 16); 50% of anything above it is
 *     assessable income. Under 60 the payment is taxed at marginal rates — modelled here as fully
 *     assessable, without the 15% offset a taxed‑source pension attracts, which errs on the side of
 *     MORE tax (D.3; the clients this module serves are over 60).
 *   - REVERSION: on the member's death a set share continues to the spouse for life (UniSuper:
 *     62.5%). Recorded and disclosed; the projection does not model a death.
 *
 * The aged care means test takes its income and asset definitions from these same rules (see
 * agedCareAssessableIncome.ts), so the treatment flows through to the fees; that flow‑through is on
 * the reviewer's list to confirm (F_GAPS).
 */
import { DEFINED_BENEFIT_INCOME_CAP } from './constants/ratesAndThresholds.ts';
import type { OtherIncomeItem } from './factFindTypes.ts';

/** The CPI assumption a pension's valuation factor column is read at where no rate of its own is recorded (D.2). */
export const DEFAULT_DB_INDEXATION_PCT = 2.5;

/** The share of the gross payment the deductible amount may not exceed, from 1 Jan 2016. */
export const DEFINED_BENEFIT_DEDUCTIBLE_CAP_SHARE = 0.10;

export type DefinedBenefitFundId = 'unisuper' | 'css' | 'pss' | 'dfrdb' | 'militarySuper' | 'stateScheme' | 'other';

export interface DefinedBenefitFundPreset {
    id: DefinedBenefitFundId;
    name: string;
    /** The pension product predates 20 Sep 1998 (SS Guide 4.9.2.20), so the pension is asset‑test exempt. */
    preSept1998Product: boolean;
    /** DFRDB / MilitarySuper: exempt from the assets test outright, and from the 10% cap. */
    military: boolean;
    /** The share that continues to a spouse on death, as the scheme's rules fix it (null = ask). */
    reversionPct: number | null;
    /** Indexed to CPI each year. */
    indexed: boolean;
    /** Paid from a taxed source (UniSuper) or an untaxed one (the public sector schemes). */
    taxedSource: boolean;
    note: string;
}

/**
 * The funds the profile offers by name. Picking one presets every rule below, so an adviser enters a
 * payment and a tax‑free figure and nothing else. "Other" asks the two questions the rules turn on.
 */
export const DEFINED_BENEFIT_FUNDS: readonly DefinedBenefitFundPreset[] = [
    { id: 'unisuper', name: 'UniSuper Defined Benefit Indexed Pension', preSept1998Product: true, military: false, reversionPct: 62.5, indexed: true, taxedSource: true, note: 'Open to members in the Defined Benefit Division since before 1 July 1998. A spouse automatically receives 62.5% for life.' },
    { id: 'css', name: 'CSS (Commonwealth Superannuation Scheme)', preSept1998Product: true, military: false, reversionPct: 67, indexed: true, taxedSource: false, note: 'Closed to new members since 1990. A spouse generally receives 67%.' },
    { id: 'pss', name: 'PSS (Public Sector Superannuation Scheme)', preSept1998Product: true, military: false, reversionPct: 67, indexed: true, taxedSource: false, note: 'Closed to new members since 2005. A spouse generally receives 67%.' },
    { id: 'dfrdb', name: 'DFRDB (Defence Force Retirement and Death Benefits)', preSept1998Product: true, military: true, reversionPct: 62.5, indexed: true, taxedSource: false, note: 'A military pension: not counted as an asset, and the tax‑free part is not capped for the income test.' },
    { id: 'militarySuper', name: 'MilitarySuper (Military Superannuation and Benefits Scheme)', preSept1998Product: true, military: true, reversionPct: 62.5, indexed: true, taxedSource: false, note: 'A military pension: not counted as an asset, and the tax‑free part is not capped for the income test.' },
    { id: 'stateScheme', name: 'A state government scheme (e.g. State Super, QSuper, ESSSuper, Super SA)', preSept1998Product: true, military: false, reversionPct: null, indexed: true, taxedSource: false, note: 'The long‑standing state schemes predate 1998; check the spouse share on the scheme’s statement.' },
    { id: 'other', name: 'Another fund', preSept1998Product: false, military: false, reversionPct: null, indexed: true, taxedSource: true, note: 'Whether the pension counts as an asset turns on whether the pension product existed before 20 September 1998.' },
];

export function definedBenefitFundPreset(fundId: unknown): DefinedBenefitFundPreset {
    return DEFINED_BENEFIT_FUNDS.find((f) => f.id === fundId) ?? DEFINED_BENEFIT_FUNDS[DEFINED_BENEFIT_FUNDS.length - 1]!;
}

export function isDefinedBenefitPension(item: Pick<OtherIncomeItem, 'type'> | null | undefined): boolean {
    return item?.type === 'definedBenefitPension';
}

export type DefinedBenefitPaymentFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'annually';
const PERIODS_PER_YEAR: Record<DefinedBenefitPaymentFrequency, number> = { weekly: 52, fortnightly: 26, monthly: 12, annually: 1 };

/** The yearly figure from the payment as it appears on the payment advice. */
export function annualFromPayment(perPayment: unknown, frequency: unknown): number {
    const p = Number(perPayment);
    if (!Number.isFinite(p) || p <= 0) return 0;
    const periods = PERIODS_PER_YEAR[(frequency as DefinedBenefitPaymentFrequency) || 'fortnightly'] ?? 26;
    return p * periods;
}

const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : 0;
};

/** The annual payment the rules run on. */
export function definedBenefitAnnualPayment(item: OtherIncomeItem): number {
    return Math.max(0, num(item.amount));
}

/** The deductible amount the fund reports (its tax‑free portion), annual; blank reads as nothing. */
export function definedBenefitDeductibleAmount(item: OtherIncomeItem): number {
    return Math.max(0, num(item.dbDeductibleAmount));
}

export function definedBenefitIsMilitary(item: OtherIncomeItem): boolean {
    if (typeof item.dbMilitary === 'boolean') return item.dbMilitary;
    return definedBenefitFundPreset(item.dbFund).military;
}

/** Whether the pension product predates 20 Sep 1998 — the exemption's hinge. Explicit answer wins over the fund's preset. */
export function definedBenefitProductPredates1998(item: OtherIncomeItem): boolean {
    if (typeof item.dbPreSept1998Product === 'boolean') return item.dbPreSept1998Product;
    return definedBenefitFundPreset(item.dbFund).preSept1998Product;
}

/**
 * INCOME TEST (SS Guide 4.9.3.30): the payment less its deductible amount, the deductible capped at
 * 10% of the gross payment from 1 Jan 2016 — except for the military schemes.
 */
export function definedBenefitAssessableIncome(item: OtherIncomeItem): number {
    const payment = definedBenefitAnnualPayment(item);
    const deductible = definedBenefitDeductibleAmount(item);
    const cap = definedBenefitIsMilitary(item) ? deductible : Math.min(deductible, payment * DEFINED_BENEFIT_DEDUCTIBLE_CAP_SHARE);
    return Math.max(0, payment - cap);
}

/** ASSETS TEST (SS Guide 4.9.2.20): exempt where the product predates 20 Sep 1998, or the scheme is military. */
export function definedBenefitAssetTestExempt(item: OtherIncomeItem): boolean {
    return definedBenefitIsMilitary(item) || definedBenefitProductPredates1998(item);
}

/**
 * The pension valuation factors (SS Guide 4.9.5.60; Social Security (Pension Valuation Factor)
 * Determination 2018; guide page last reviewed 22 Sep 2025). Rows are age NEXT BIRTHDAY bands;
 * columns are the pension's indexation‑rate bands, highest first:
 *   ≥8% · 7–8% · 6–7% · 5–6% · 4–5% · 3–4% · 2.5–3% · 2–2.5% · 1–2% · <1%
 */
export const PENSION_VALUATION_FACTORS: ReadonlyArray<{ maxAgeNextBirthday: number; factors: readonly number[] }> = [
    { maxAgeNextBirthday: 20, factors: [82, 60, 45, 35, 28, 23, 21, 19, 16, 14] },
    { maxAgeNextBirthday: 25, factors: [74, 55, 42, 33, 27, 22, 20, 19, 16, 14] },
    { maxAgeNextBirthday: 30, factors: [66, 51, 40, 32, 26, 21, 20, 18, 16, 14] },
    { maxAgeNextBirthday: 35, factors: [58, 46, 37, 30, 25, 21, 19, 18, 15, 13] },
    { maxAgeNextBirthday: 40, factors: [51, 41, 33, 28, 23, 20, 18, 17, 15, 13] },
    { maxAgeNextBirthday: 45, factors: [44, 36, 30, 25, 22, 19, 17, 16, 14, 13] },
    { maxAgeNextBirthday: 50, factors: [38, 32, 27, 23, 20, 17, 16, 15, 13, 12] },
    { maxAgeNextBirthday: 55, factors: [32, 27, 24, 21, 18, 16, 15, 14, 13, 11] },
    { maxAgeNextBirthday: 60, factors: [26, 23, 20, 18, 16, 14, 14, 13, 12, 11] },
    { maxAgeNextBirthday: 65, factors: [21, 19, 17, 15, 14, 12, 12, 11, 10, 10] },
    { maxAgeNextBirthday: 70, factors: [16, 15, 14, 12, 11, 11, 10, 10, 9, 8] },
    { maxAgeNextBirthday: 75, factors: [12, 11, 10, 10, 9, 8, 8, 8, 7, 7] },
    { maxAgeNextBirthday: 80, factors: [9, 9, 8, 8, 7, 7, 7, 7, 6, 6] },
    { maxAgeNextBirthday: 85, factors: [6, 6, 5, 5, 5, 5, 5, 4, 4, 4] },
    { maxAgeNextBirthday: 90, factors: [4, 4, 4, 3, 3, 3, 3, 3, 3, 3] },
    { maxAgeNextBirthday: 95, factors: [3, 3, 3, 3, 3, 3, 3, 3, 3, 3] },
    { maxAgeNextBirthday: 100, factors: [3, 2, 2, 2, 2, 2, 2, 2, 2, 2] },
    { maxAgeNextBirthday: Infinity, factors: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1] },
];

/** The column of the table a pension's indexation rate (% p.a.) falls in. */
export function pensionValuationIndexationColumn(indexationPct: number): number {
    const r = Number.isFinite(indexationPct) ? indexationPct : 0;
    if (r >= 8) return 0;
    if (r >= 7) return 1;
    if (r >= 6) return 2;
    if (r >= 5) return 3;
    if (r >= 4) return 4;
    if (r >= 3) return 5;
    if (r >= 2.5) return 6;
    if (r >= 2) return 7;
    if (r >= 1) return 8;
    return 9;
}

/** The factor for a person of this age (today — the table reads age NEXT birthday) and a pension indexed at this rate. */
export function pensionValuationFactor(age: number, indexationPct: number): number {
    const ageNextBirthday = Math.floor(Math.max(0, age)) + 1;
    const row = PENSION_VALUATION_FACTORS.find((r) => ageNextBirthday <= r.maxAgeNextBirthday) ?? PENSION_VALUATION_FACTORS[PENSION_VALUATION_FACTORS.length - 1]!;
    return row.factors[pensionValuationIndexationColumn(indexationPct)]!;
}

/** The indexation rate the pension carries: its own figure, else the run's CPI assumption where it is indexed, else nil. */
export function definedBenefitIndexationPct(item: OtherIncomeItem, cpiPct: number): number {
    const indexed = typeof item.dbIndexed === 'boolean' ? item.dbIndexed : definedBenefitFundPreset(item.dbFund).indexed;
    if (!indexed) return 0;
    const own = num(item.dbIndexationPct);
    return own > 0 ? own : Math.max(0, cpiPct);
}

/**
 * ASSETS TEST, a pension that is counted: annual payment × the pension valuation factor. Nil where
 * exempt, or where the owner's age is unknown (a value cannot be struck on a guessed age).
 */
export function definedBenefitAssetValue(item: OtherIncomeItem, ownerAge: number | null | undefined, cpiPct: number): number {
    if (definedBenefitAssetTestExempt(item)) return 0;
    if (ownerAge == null || !Number.isFinite(ownerAge)) return 0;
    return definedBenefitAnnualPayment(item) * pensionValuationFactor(ownerAge, definedBenefitIndexationPct(item, cpiPct));
}

/**
 * Paid from a taxed source (UniSuper and most funds) or an untaxed one (CSS, PSS, the military and
 * state schemes). An explicit answer wins over the fund's preset.
 */
export function definedBenefitTaxedSource(item: OtherIncomeItem): boolean {
    if (typeof item.dbTaxedSource === 'boolean') return item.dbTaxedSource;
    return definedBenefitFundPreset(item.dbFund).taxedSource;
}

/**
 * TAX (ITAA 1997 Divisions 301 and 303‑A; the defined benefit income cap, D.1.19). The tax‑free part
 * (the fund's tax‑free component, which is also its Centrelink deductible amount) is never taxed; the
 * rest is the taxable part.
 *  - A TAXED source from 60: tax‑free up to the cap, with 50% of the payment above the cap
 *    assessable at marginal rates and no offset.
 *  - An UNTAXED source from 60 (CSS, PSS, the military and state schemes): the whole taxable part is
 *    assessable at marginal rates, with a 10% tax offset on the part up to the cap
 *    ({@link definedBenefitTaxOffset}).
 *  - Under 60, or age unknown: the taxable part is assessable, no offset. (The 15% offset on a taxed
 *    source between preservation age and 60 is not modelled: everyone under 60 today was born after
 *    30 June 1964 and so has a preservation age of 60 — it applies to no one.)
 */
export function definedBenefitTaxableIncome(item: OtherIncomeItem, ownerAge: number | null | undefined, cap: number = DEFINED_BENEFIT_INCOME_CAP): number {
    const payment = definedBenefitAnnualPayment(item);
    if (ownerAge != null && ownerAge >= 60 && definedBenefitTaxedSource(item)) return Math.max(0, payment - cap) * 0.5;
    return Math.max(0, payment - definedBenefitDeductibleAmount(item));
}

/**
 * The 10% tax offset on an untaxed‑source pension from 60: 10% of the taxable part, but only on the
 * part that sits under the cap once the tax‑free part (which counts toward the cap too) is allowed
 * for. Nil for a taxed source, under 60, or age unknown. Non‑refundable — the tax engine caps it at
 * the tax standing.
 */
export function definedBenefitTaxOffset(item: OtherIncomeItem, ownerAge: number | null | undefined, cap: number = DEFINED_BENEFIT_INCOME_CAP): number {
    if (ownerAge == null || ownerAge < 60 || definedBenefitTaxedSource(item)) return 0;
    const taxablePart = definedBenefitTaxableIncome(item, ownerAge, cap);
    const underCap = Math.max(0, Math.min(taxablePart, cap - definedBenefitDeductibleAmount(item)));
    return 0.1 * underCap;
}

/** "CSS", "PSS", "UniSuper" — the fund as a client would say it; "the fund" where none is named. */
export function definedBenefitFundShortName(item: OtherIncomeItem): string {
    const preset = definedBenefitFundPreset(item.dbFund);
    if (preset.id === 'other' || preset.id === 'stateScheme') return 'the fund';
    return preset.name.split(' ')[0] ?? 'the fund';
}

/** The share that continues to a spouse on the member's death, as a percentage (null where not recorded). */
export function definedBenefitReversionPct(item: OtherIncomeItem): number | null {
    const own = item.dbReversionPct;
    if (typeof own === 'number' && Number.isFinite(own)) return Math.max(0, Math.min(100, own));
    return definedBenefitFundPreset(item.dbFund).reversionPct;
}

export function definedBenefitReversionAmount(item: OtherIncomeItem): number | null {
    const pct = definedBenefitReversionPct(item);
    return pct == null ? null : (definedBenefitAnnualPayment(item) * pct) / 100;
}

/**
 * The treatment in plain words, for the panel beside the profile entry and the advice document. Each
 * sentence is a consequence of what was entered; the user never sees a rule they have to apply.
 */
export function describeDefinedBenefitTreatment(
    item: OtherIncomeItem,
    input: { ownerName: string; ownerAge: number | null | undefined; partnerName?: string | null; cpiPct: number; money: (n: number) => string },
): string[] {
    const payment = definedBenefitAnnualPayment(item);
    if (payment <= 0) return [];
    const { ownerName, ownerAge, partnerName, cpiPct, money } = input;
    const out: string[] = [];
    if (definedBenefitAssetTestExempt(item)) {
        out.push(`Centrelink does not count this pension as an asset.`);
    } else if (ownerAge == null) {
        out.push(`Centrelink counts this pension as an asset, valued from ${ownerName}'s age. Enter a date of birth to see the figure.`);
    } else {
        const value = definedBenefitAssetValue(item, ownerAge, cpiPct);
        out.push(`Centrelink counts this pension as worth about ${money(Math.round(value))} while ${ownerName} is ${Math.floor(ownerAge)}, falling each year with age. It is not part of the estate.`);
    }
    const assessable = definedBenefitAssessableIncome(item);
    const deductible = definedBenefitDeductibleAmount(item);
    if (deductible <= 0) {
        out.push(`The whole ${money(Math.round(payment))} counts as income each year, because no tax-free part was entered.`);
    } else if (definedBenefitIsMilitary(item)) {
        out.push(`${money(Math.round(assessable))} of the ${money(Math.round(payment))} counts as income each year (the tax-free part is not capped for a military pension).`);
    } else {
        out.push(`${money(Math.round(assessable))} of the ${money(Math.round(payment))} counts as income each year (the tax-free part counts up to 10% of the payment).`);
    }
    const taxable = definedBenefitTaxableIncome(item, ownerAge);
    const taxedSource = definedBenefitTaxedSource(item);
    if (ownerAge != null && ownerAge >= 60 && !taxedSource) {
        const offset = definedBenefitTaxOffset(item, ownerAge);
        const fund = definedBenefitFundShortName(item);
        out.push(`${money(Math.round(taxable))} of it is taxed as income at ${ownerName}'s marginal rate, less a 10% tax offset of ${money(Math.round(offset))}, because ${fund} pays it from an untaxed source.`);
    } else if (ownerAge != null && ownerAge >= 60) {
        out.push(taxable > 0
            ? `It is tax-free up to ${money(DEFINED_BENEFIT_INCOME_CAP)} a year; half of the ${money(Math.round(payment - DEFINED_BENEFIT_INCOME_CAP))} above that is taxed.`
            : `It is tax-free, being under the ${money(DEFINED_BENEFIT_INCOME_CAP)} a year cap.`);
    } else if (!taxedSource) {
        out.push(`The payment is taxed as income at ${ownerName}'s marginal rate; from 60 a 10% tax offset applies, because it is paid from an untaxed source.`);
    } else {
        out.push(`Under 60 the payment is taxed as income; from 60 it is tax-free up to ${money(DEFINED_BENEFIT_INCOME_CAP)} a year.`);
    }
    const reversion = definedBenefitReversionAmount(item);
    if (partnerName && reversion != null) {
        out.push(`If ${ownerName} dies first, ${partnerName} receives ${money(Math.round(reversion))} a year for life.`);
    } else if (partnerName) {
        out.push(`Enter the share that continues to ${partnerName} if ${ownerName} dies first; it is on the fund's statement.`);
    } else {
        out.push(`It stops at death; nothing passes to the estate.`);
    }
    return out;
}
